import { tokens } from '../../theme/tokens';

const { surface, line, ink, accent, signal, shadow, font } = tokens;

/**
 * Design tokens for the Data Ingestion console.
 *
 * Originally transcribed from the "Data Ingestion Console" handoff. The
 * names are kept for the console's components; the values now come from
 * `theme/tokens.ts`, so the console's accent, greys and health tones match
 * the rest of the app. The quiet greys the handoff used for real content are
 * mapped to ink tones that clear 4.5:1 on every surface they sit on.
 */
export const ingest = {
  color: {
    surface: surface.card,
    /** Rail, table headers, expanded run panels. */
    sunken: surface.subtle,
    /** Expanded row background. */
    rowOpen: surface.sunken,
    border: line.default,
    borderInner: line.hairline,
    borderFaint: line.faint,
    borderFaintAlt: line.faint,
    control: line.strong,
    ink: ink.primary,
    body: ink.body,
    bodyAlt: ink.bodyAlt,
    muted: ink.muted,
    faint: ink.muted,
    faintAlt: ink.muted,
    dim: ink.bodyAlt,
    placeholder: ink.muted,
    accent: accent.main,
    accentHover: accent.hover,
    accentTint: accent.tint,
    /** Filter chip fill when selected. */
    chipOn: surface.header,
    chipOnCount: ink.faint,
    chipOnDivider: ink.body,
    chipOnBody: ink.onDark,
    success: signal.positive.solid,
    successInk: signal.positive.ink,
    successTint: signal.positive.tint,
    successTintSoft: signal.positive.row,
    danger: signal.problem.solid,
    dangerInk: signal.problem.ink,
    dangerTint: signal.problem.tint,
    dangerTintSoft: signal.problem.row,
    dangerBorder: signal.problem.border,
    dangerGlyph: signal.problem.solid,
    dangerMeta: signal.problem.ink,
    dangerBody: signal.problem.ink,
    dangerControl: signal.problem.border,
    warning: signal.caution.solid,
    warningInk: signal.caution.ink,
    warningTint: signal.caution.tint,
    queuedTint: signal.caution.tint,
    queuedInk: signal.caution.ink,
    /** Sparkline bar with no run behind it. */
    idle: line.default,
    /** Paused health dot, a UI indicator held to 3:1, not 4.5:1. */
    dotMuted: ink.disabled,
    attentionHeadBg: signal.caution.row,
    attentionHeadBorder: signal.caution.border,
    attentionHeadInk: signal.caution.ink,
    attentionHeadCount: signal.caution.solid,
  },
  font: {
    sans: font.sans,
    /** Logs, key caps, error bodies, PDP URLs. */
    mono: font.mono,
  },
  size: {
    rail: 330,
    /** The console card is a fixed-height workspace, not a growing page. */
    card: 900,
  },
  shadow: {
    card: shadow.card,
  },
} as const;

/** Numeric columns line up only with tabular figures. */
export const tabular = { fontVariantNumeric: 'tabular-nums' } as const;

export const ellipsis = {
  minWidth: 0,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
} as const;

/** Secondary button: 32px, hairline border, accent on hover. */
export const secondaryButton = {
  height: 32,
  minHeight: 32,
  px: '12px',
  border: `1px solid ${ingest.color.control}`,
  borderRadius: `${tokens.radius.small}px`,
  background: ingest.color.surface,
  fontFamily: ingest.font.sans,
  fontSize: '12.5px',
  fontWeight: 400,
  lineHeight: 1,
  color: ingest.color.body,
  textTransform: 'none',
  whiteSpace: 'nowrap',
  '&:hover': {
    background: ingest.color.surface,
    borderColor: ingest.color.accent,
    color: ingest.color.accentHover,
  },
  '&.Mui-disabled': {
    borderColor: ingest.color.borderInner,
    color: ingest.color.placeholder,
  },
} as const;

/** Primary button: filled accent, no border. */
export const primaryButton = {
  height: 32,
  minHeight: 32,
  px: '13px',
  border: 0,
  borderRadius: `${tokens.radius.small}px`,
  background: ingest.color.accent,
  fontFamily: ingest.font.sans,
  fontSize: '12.5px',
  fontWeight: 500,
  lineHeight: 1,
  color: ingest.color.surface,
  textTransform: 'none',
  whiteSpace: 'nowrap',
  boxShadow: 'none',
  '&:hover': { background: ingest.color.accentHover, boxShadow: 'none' },
  '&.Mui-disabled': { background: ingest.color.control, color: ingest.color.surface },
} as const;

/** Small button inside a row (Scrape, View log). */
export const rowButton = {
  height: 26,
  minHeight: 26,
  minWidth: 0,
  px: '10px',
  border: `1px solid ${ingest.color.control}`,
  borderRadius: `${tokens.radius.chip}px`,
  background: ingest.color.surface,
  fontFamily: ingest.font.sans,
  fontSize: '12px',
  fontWeight: 400,
  lineHeight: 1,
  color: ingest.color.body,
  textTransform: 'none',
  whiteSpace: 'nowrap',
  '&:hover': {
    background: ingest.color.surface,
    borderColor: ingest.color.accent,
    color: ingest.color.accentHover,
  },
  '&.Mui-disabled': { borderColor: ingest.color.borderInner, color: ingest.color.placeholder },
} as const;

/** Uppercase 10.5px group and table headers. */
export const groupHeading = {
  fontFamily: ingest.font.sans,
  fontSize: '10.5px',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  lineHeight: 1.2,
} as const;

/** Uppercase 11px card labels. */
export const cardLabel = {
  ...groupHeading,
  fontSize: '11px',
  color: ingest.color.faintAlt,
} as const;

export type ChipTone = {
  bg: string;
  border: string;
  ink: string;
  countInk: string;
  weight: number;
};

/** Filter chip tone — dark fill when selected, hairline outline when not. */
export function chipTone(selected: boolean): ChipTone {
  return selected
    ? {
        bg: ingest.color.chipOn,
        border: ingest.color.chipOn,
        ink: ingest.color.surface,
        countInk: ingest.color.chipOnCount,
        weight: 600,
      }
    : {
        bg: ingest.color.surface,
        border: ingest.color.control,
        ink: ingest.color.bodyAlt,
        countInk: ingest.color.faintAlt,
        weight: 400,
      };
}
